/** Presentation helpers. Money arrives from the API as decimal strings. */

const SYMBOLS: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };

export function money(value: string | number | null | undefined, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${SYMBOLS[currency] ?? ''}${n.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Compact form for headline tiles, where two decimals are noise. */
export function moneyShort(value: string | number | null | undefined, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  const sym = SYMBOLS[currency] ?? '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}m`;
  if (abs >= 10_000) return `${sym}${Math.round(n / 1000).toLocaleString('en-GB')}k`;
  return `${sym}${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

export function pct(value: string | number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toLocaleString('en-GB', { maximumFractionDigits: dp })}%`;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Valuation dates are civil dates — never shifted by a timezone. */
export function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[Number(m) - 1]?.slice(0, 3)} ${y}`;
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  return `${shortDate(iso)} ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return shortDate(iso);
}

/** "gate.passed" -> "Gate passed" */
export function humaniseAction(action: string): string {
  const text = action.replace(/[._]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
